import { useState } from "react";
import Confetti from "../../components/Confetti";
import { track } from "../../lib/track";
import { SCENARIOS, type Scenario, type ScenarioTopic, type ScenarioTier } from "../../data/scenarios";

const TOPIC_META: Record<ScenarioTopic, { label: string; emoji: string; color: string; dark: string; bg: string }> = {
  saving:    { label: "Saving",    emoji: "🏦", color: "#16a34a", dark: "#14532d", bg: "#f0fdf4" },
  spending:  { label: "Spending",  emoji: "🛍️", color: "#f97316", dark: "#c2410c", bg: "#fff7ed" },
  earning:   { label: "Earning",   emoji: "💼", color: "#0ea5e9", dark: "#0369a1", bg: "#f0f9ff" },
  budgeting: { label: "Budgeting", emoji: "📊", color: "#a855f7", dark: "#7e22ce", bg: "#faf5ff" },
  investing: { label: "Investing", emoji: "📈", color: "#10b981", dark: "#065f46", bg: "#ecfdf5" },
  credit:    { label: "Credit",    emoji: "💳", color: "#ef4444", dark: "#991b1b", bg: "#fff1f2" },
  giving:    { label: "Giving",    emoji: "🎁", color: "#ec4899", dark: "#9d174d", bg: "#fdf2f8" },
  banking:   { label: "Banking",   emoji: "🏛️", color: "#6366f1", dark: "#3730a3", bg: "#eef2ff" },
};

const TOPICS: ScenarioTopic[] = ["saving","spending","earning","budgeting","investing","credit","giving","banking"];

function outcomeStyle(outcome: string, color: string, dark: string, isDark: boolean) {
  if (outcome === "great") return { color, bg: `${color}18`, border: `2.5px solid ${color}` };
  if (outcome === "good")  return { color: "#d97706", bg: isDark ? "#451a03" : "#fffbeb", border: "2.5px solid #f59e0b" };
  return { color: isDark ? "#fca5a5" : "#991b1b", bg: isDark ? "#450a0a" : "#fee2e2", border: "2.5px solid #ef4444" };
}

function ScenarioDetail({ scenario, isDark, onBack }: { scenario: Scenario; isDark: boolean; onBack: () => void }) {
  const meta = TOPIC_META[scenario.topic];
  const color = meta.color;
  const dark  = meta.dark;

  const [phase, setPhase]               = useState<"setup"|"consequence"|"quiz">("setup");
  const [pick, setPick]                 = useState<number|null>(null);
  const [showAll, setShowAll]           = useState(false);
  const [qIdx, setQIdx]                 = useState(0);
  const [answers, setAnswers]           = useState<(number|null)[]>([]);
  const [submitted, setSubmitted]       = useState(false);
  const [showConf, setShowConf]         = useState(false);

  const qs     = scenario.quiz;
  const curQ   = qs[qIdx];
  const curAns = answers[qIdx] ?? null;
  const isLastQ = qIdx === qs.length - 1;
  const score   = qs.filter((q,i) => answers[i] === q.a).length;

  function selectAnswer(i: number) {
    if (submitted) return;
    setAnswers(prev => { const n = [...prev]; n[qIdx] = i; return n; });
  }
  function submitCurrent() {
    if (curAns === null) return;
    setSubmitted(true);
    if (isLastQ) track("scene_completed", { topic: scenario.topic });
    if (curAns === curQ.a && isLastQ) { setShowConf(true); setTimeout(() => setShowConf(false), 2000); }
  }
  function nextQ() { setSubmitted(false); setQIdx(q => q + 1); }

  const bgColor  = isDark ? "#0f172a" : meta.bg;
  const cardBg   = isDark ? "#1e293b" : "#fff";
  const cardBorder = isDark ? `2px solid #334155` : `3px solid ${color}30`;
  const textMain = isDark ? "#f1f5f9" : "#1f2937";
  const textSub  = isDark ? "#94a3b8" : "#6b7280";

  return (
    <div style={{ fontFamily: "Nunito", minHeight: "100vh", background: bgColor, paddingBottom: 30 }}>
      {showConf && <Confetti />}

      {/* Top bar */}
      <div style={{
        background: isDark ? "#1e293b" : "#fff",
        borderBottom: isDark ? "1px solid #334155" : `3px solid ${color}55`,
        padding: "12px 16px", display: "flex", alignItems: "center", gap: 12,
        position: "sticky", top: 0, zIndex: 10,
        boxShadow: isDark ? "0 2px 12px #00000055" : "0 2px 12px #0001",
      }}>
        <button onClick={onBack} className="btn-bounce" style={{
          background: `${color}22`, border: `2px solid ${color}55`,
          borderRadius: 12, padding: "6px 12px", fontSize: 13, fontWeight: 800,
          color: isDark ? "#e2e8f0" : dark, cursor: "pointer",
        }}>← Back</button>
        <div style={{ flex: 1 }}>
          <div style={{ fontSize: 10, color, fontWeight: 900, textTransform: "uppercase", letterSpacing: 1.5 }}>
            {meta.emoji} {meta.label}
          </div>
          <div style={{ fontSize: 15, fontWeight: 900, color: isDark ? "#f1f5f9" : dark, fontFamily: "Fredoka One", lineHeight: 1.1 }}>
            {scenario.title}
          </div>
        </div>
      </div>

      {/* Phase dots */}
      <div style={{ display: "flex", gap: 6, padding: "12px 16px 0", justifyContent: "center" }}>
        {(["setup","consequence","quiz"] as const).map((p, i) => {
          const phaseIdx = ["setup","consequence","quiz"].indexOf(phase);
          return (
            <div key={p} style={{
              height: 8, flex: 1, maxWidth: 64, borderRadius: 8,
              background: phaseIdx >= i ? color : isDark ? "#334155" : "#d1d5db",
              transition: "background 0.4s",
              boxShadow: phaseIdx === i ? `0 2px 8px ${color}66` : "none",
            }} />
          );
        })}
      </div>
      <div style={{ display: "flex", gap: 6, padding: "3px 16px 10px", justifyContent: "center" }}>
        {["🎭 Scenario", "💡 Reveal", "🧠 Quiz"].map((lbl, i) => {
          const phaseIdx = ["setup","consequence","quiz"].indexOf(phase);
          return (
            <div key={i} style={{
              flex: 1, maxWidth: 64, textAlign: "center", fontSize: 9, fontWeight: 800,
              color: phaseIdx >= i ? color : isDark ? "#475569" : "#9ca3af",
              transition: "color 0.3s",
            }}>{lbl}</div>
          );
        })}
      </div>

      <div style={{ padding: "0 16px" }}>

        {/* SETUP */}
        {phase === "setup" && (
          <div style={{ animation: "slideUp 0.4s" }}>
            <div style={{ textAlign: "center", marginBottom: 16 }}>
              <div style={{ fontSize: 36, marginBottom: 4 }}>🎭</div>
              <div style={{ fontSize: 12, fontWeight: 900, color, textTransform: "uppercase", letterSpacing: 1.5 }}>
                What Would You Do?
              </div>
            </div>
            <div style={{
              background: cardBg, borderRadius: 22, padding: "20px 18px",
              border: cardBorder,
              boxShadow: isDark ? "0 8px 28px #00000055" : `0 8px 28px ${color}22`,
              marginBottom: 16,
            }}>
              <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 14 }}>
                <div style={{ fontSize: 32 }}>{scenario.characterEmoji}</div>
                <div style={{ fontSize: 14, fontWeight: 800, color: textSub }}>{scenario.character}</div>
              </div>
              <div style={{ fontSize: 15, fontWeight: 700, color: textMain, lineHeight: 1.65 }}>
                {scenario.setup}
              </div>
            </div>
            <div style={{ fontSize: 11, fontWeight: 900, color: isDark ? "#94a3b8" : color, textTransform: "uppercase", letterSpacing: 1.5, marginBottom: 10, paddingLeft: 2 }}>
              Choose your path:
            </div>
            <div style={{ display: "flex", flexDirection: "column", gap: 10, marginBottom: 16 }}>
              {scenario.choices.map((c, i) => {
                const picked = pick === i;
                return (
                  <button key={i} onClick={() => setPick(i)} className="btn-bounce" style={{
                    display: "flex", alignItems: "center", gap: 12,
                    background: picked ? `${color}22` : isDark ? "#1e293b" : "#f9fafb",
                    border: picked ? `2.5px solid ${color}` : isDark ? "2px solid #334155" : "2px solid #e5e7eb",
                    borderRadius: 16, padding: "15px 14px", cursor: "pointer", textAlign: "left",
                    transition: "all 0.2s",
                    boxShadow: picked ? `0 4px 14px ${color}33` : "none",
                  }}>
                    <div style={{ fontSize: 28, flexShrink: 0 }}>{c.emoji}</div>
                    <div style={{ fontSize: 15, fontWeight: 700, color: isDark ? "#e2e8f0" : "#374151", lineHeight: 1.4 }}>{c.label}</div>
                  </button>
                );
              })}
            </div>
            <button onClick={() => setPhase("consequence")} disabled={pick === null} className="btn-bounce" style={{
              width: "100%",
              background: pick !== null ? `linear-gradient(135deg,${color},${dark})` : isDark ? "#1e293b" : "#d1d5db",
              border: "none", borderRadius: 18, padding: "17px", fontSize: 18, fontWeight: 900,
              color: pick !== null ? "#fff" : isDark ? "#475569" : "#9ca3af",
              cursor: pick !== null ? "pointer" : "not-allowed", fontFamily: "Fredoka One",
              boxShadow: pick !== null ? `0 8px 24px ${color}55` : "none", transition: "all 0.3s",
            }}>
              See What Happens →
            </button>
          </div>
        )}

        {/* CONSEQUENCE */}
        {phase === "consequence" && pick !== null && (() => {
          const chosen = scenario.choices[pick];
          const os = outcomeStyle(chosen.outcome, color, dark, isDark);
          return (
            <div style={{ animation: "slideUp 0.4s" }}>
              <div style={{ textAlign: "center", marginBottom: 14 }}>
                <div style={{
                  display: "inline-block", background: os.bg, border: os.border,
                  borderRadius: 20, padding: "6px 18px", fontSize: 14, fontWeight: 900, color: os.color,
                }}>
                  {chosen.outcomeLabel}
                </div>
              </div>
              <div style={{
                background: cardBg, borderRadius: 22, padding: "20px 18px",
                border: os.border,
                boxShadow: isDark ? "0 8px 28px #00000055" : `0 8px 28px ${color}22`,
                marginBottom: 12,
              }}>
                <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 12 }}>
                  <div style={{ fontSize: 28 }}>{chosen.emoji}</div>
                  <div style={{ fontSize: 14, fontWeight: 800, color: textSub }}>
                    You chose: <span style={{ color: os.color }}>{chosen.label}</span>
                  </div>
                </div>
                <div style={{ fontSize: 15, fontWeight: 700, color: isDark ? "#e2e8f0" : "#374151", lineHeight: 1.65 }}>
                  {chosen.consequence}
                </div>
              </div>

              <button onClick={() => setShowAll(s => !s)} style={{
                background: "none", border: `2px solid ${color}44`, borderRadius: 12,
                padding: "8px 14px", fontSize: 13, fontWeight: 800,
                color: isDark ? "#94a3b8" : color, cursor: "pointer", marginBottom: 12, width: "100%",
              }}>
                {showAll ? "Hide other paths ▲" : "See all paths ▼"}
              </button>

              {showAll && (
                <div style={{ display: "flex", flexDirection: "column", gap: 8, marginBottom: 14 }}>
                  {scenario.choices.map((c, i) => {
                    if (i === pick) return null;
                    const cs = outcomeStyle(c.outcome, color, dark, isDark);
                    return (
                      <div key={i} style={{
                        background: isDark ? "#1e293b" : "#f9fafb",
                        borderRadius: 16, padding: "14px",
                        border: isDark ? "2px solid #334155" : "2px solid #e5e7eb",
                      }}>
                        <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 8, flexWrap: "wrap" }}>
                          <div style={{ fontSize: 22 }}>{c.emoji}</div>
                          <div style={{ fontSize: 13, fontWeight: 800, color: textSub, flex: 1 }}>{c.label}</div>
                          <div style={{ flexShrink: 0, fontSize: 11, fontWeight: 900, background: cs.bg, border: cs.border, borderRadius: 12, padding: "3px 10px", color: cs.color }}>
                            {c.outcomeLabel}
                          </div>
                        </div>
                        <div style={{ fontSize: 13, fontWeight: 700, color: textSub, lineHeight: 1.5 }}>
                          {c.consequence}
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}

              <div style={{
                background: isDark ? "#0f172a" : `linear-gradient(135deg,${color}20,${color}0a)`,
                borderRadius: 16, padding: "14px 16px", marginBottom: 16,
                border: `2px solid ${color}44`,
              }}>
                <div style={{ fontSize: 11, fontWeight: 900, color, textTransform: "uppercase", letterSpacing: 1.5, marginBottom: 6 }}>
                  💡 The Lesson
                </div>
                <div style={{ fontSize: 15, fontWeight: 800, color: isDark ? "#e2e8f0" : dark, lineHeight: 1.55 }}>
                  {scenario.lesson}
                </div>
              </div>

              <button onClick={() => setPhase("quiz")} className="btn-bounce" style={{
                width: "100%", background: `linear-gradient(135deg,${color},${dark})`,
                border: "none", borderRadius: 18, padding: "17px", fontSize: 18, fontWeight: 900,
                color: "#fff", cursor: "pointer", fontFamily: "Fredoka One", letterSpacing: 0.5,
                boxShadow: `0 8px 24px ${color}55`,
              }}>
                Take the Quiz! 🧠
              </button>
            </div>
          );
        })()}

        {/* QUIZ */}
        {phase === "quiz" && (
          <div style={{ animation: "slideUp 0.4s" }}>
            <div style={{ marginBottom: 12 }}>
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 6 }}>
                <div style={{ fontSize: 12, fontWeight: 900, color, textTransform: "uppercase", letterSpacing: 1.5 }}>
                  🧠 Question {qIdx + 1} of {qs.length}
                </div>
                <div style={{ fontSize: 12, fontWeight: 800, color: textSub }}>
                  {qs.filter((q, i) => answers[i] === q.a).length}/{qs.length} correct
                </div>
              </div>
              <div style={{ display: "flex", gap: 5 }}>
                {qs.map((_, i) => {
                  const answered = answers[i] !== undefined && answers[i] !== null;
                  const correct  = answers[i] === qs[i].a;
                  const isCur    = i === qIdx;
                  return (
                    <div key={i} style={{
                      flex: 1, height: 8, borderRadius: 6,
                      background: answered ? (correct ? color : "#f87171") : isCur ? `${color}55` : isDark ? "#334155" : "#e5e7eb",
                      border: isCur ? `2px solid ${color}` : "2px solid transparent",
                      transition: "all 0.3s",
                    }} />
                  );
                })}
              </div>
            </div>

            <div style={{
              background: cardBg, borderRadius: 22, padding: "20px 18px",
              border: cardBorder,
              boxShadow: isDark ? "0 8px 28px #00000055" : `0 8px 28px ${color}22`,
              marginBottom: 14,
            }}>
              <div style={{ fontSize: 16, fontWeight: 800, color: isDark ? "#f1f5f9" : "#1f2937", marginBottom: 16, lineHeight: 1.5 }}>
                {curQ.q}
              </div>
              <div style={{ display: "flex", flexDirection: "column", gap: 9 }}>
                {curQ.opts.map((o, i) => {
                  const letters = ["🅰️","🅱️","🅲️","🅳️"];
                  let bg = isDark ? "#0f172a" : "#f9fafb";
                  let border = isDark ? "2px solid #334155" : "2px solid #e5e7eb";
                  let clr = isDark ? "#cbd5e1" : "#374151";
                  if (submitted) {
                    if (i === curQ.a) { bg = `${color}22`; border = `2.5px solid ${color}`; clr = isDark ? "#e2e8f0" : dark; }
                    else if (i === curAns && i !== curQ.a) { bg = isDark ? "#450a0a" : "#fee2e2"; border = "2.5px solid #ef4444"; clr = isDark ? "#fca5a5" : "#991b1b"; }
                  } else if (curAns === i) { bg = `${color}20`; border = `2.5px solid ${color}`; clr = isDark ? "#e2e8f0" : dark; }
                  return (
                    <button key={i} onClick={() => selectAnswer(i)} className="btn-bounce" style={{
                      display: "flex", alignItems: "center", gap: 10, background: bg,
                      border, borderRadius: 14, padding: "13px 14px",
                      cursor: submitted ? "default" : "pointer",
                      fontSize: 14, fontWeight: 700, color: clr, textAlign: "left",
                      transition: "all 0.2s",
                      boxShadow: curAns === i && !submitted ? `0 4px 14px ${color}33` : "none",
                    }}>
                      <span style={{ fontSize: 20, flexShrink: 0 }}>{letters[i]}</span>{o}
                    </button>
                  );
                })}
              </div>
              {submitted && (
                <div style={{
                  marginTop: 14,
                  background: curAns === curQ.a ? `${color}15` : isDark ? "#451a03" : "#fffbeb",
                  border: `2.5px solid ${curAns === curQ.a ? color : "#f59e0b"}`,
                  borderRadius: 16, padding: "12px 14px", animation: "pop 0.4s",
                }}>
                  <div style={{ fontSize: 14, fontWeight: 900, lineHeight: 1.4, color: curAns === curQ.a ? (isDark ? "#e2e8f0" : dark) : isDark ? "#fbbf24" : "#92400e" }}>
                    {curAns === curQ.a
                      ? `🎉 ${curQ.praise}`
                      : `💡 The answer was: "${curQ.opts[curQ.a]}" — keep going!`}
                  </div>
                </div>
              )}
            </div>

            {!submitted ? (
              <button onClick={submitCurrent} disabled={curAns === null} className="btn-bounce" style={{
                width: "100%",
                background: curAns === null ? (isDark ? "#1e293b" : "#d1d5db") : `linear-gradient(135deg,${color},${dark})`,
                border: "none", borderRadius: 18, padding: "17px", fontSize: 18, fontWeight: 900,
                color: curAns === null ? (isDark ? "#475569" : "#fff") : "#fff",
                cursor: curAns === null ? "not-allowed" : "pointer", fontFamily: "Fredoka One",
                boxShadow: curAns !== null ? `0 8px 24px ${color}55` : "none", transition: "all 0.3s",
              }}>
                Check Answer! ✅
              </button>
            ) : isLastQ ? (
              <div>
                <div style={{
                  background: cardBg, borderRadius: 18, padding: "16px",
                  border: isDark ? "2px solid #334155" : `3px solid ${color}44`,
                  marginBottom: 12, textAlign: "center",
                  boxShadow: isDark ? "0 6px 20px #00000055" : `0 6px 20px ${color}22`,
                  animation: "pop 0.4s",
                }}>
                  <div style={{ fontSize: 36, marginBottom: 6 }}>
                    {score === qs.length ? "🏆" : score >= qs.length / 2 ? "⭐" : "📚"}
                  </div>
                  <div style={{ fontSize: 20, fontWeight: 900, color: isDark ? "#f1f5f9" : dark, fontFamily: "Fredoka One" }}>
                    {score === qs.length ? "Perfect!" : score >= qs.length / 2 ? "Nice Work!" : "Keep Practicing!"}
                  </div>
                  <div style={{ fontSize: 15, color: textSub, fontWeight: 700, marginTop: 4 }}>
                    {score}/{qs.length} correct
                  </div>
                </div>
                <button onClick={onBack} className="btn-bounce" style={{
                  width: "100%", background: `linear-gradient(135deg,${color},${dark})`,
                  border: "none", borderRadius: 18, padding: "17px", fontSize: 18, fontWeight: 900,
                  color: "#fff", cursor: "pointer", fontFamily: "Fredoka One",
                  boxShadow: `0 8px 24px ${color}55`, animation: "pulse 1.2s infinite",
                }}>
                  Back to Scenarios →
                </button>
              </div>
            ) : (
              <button onClick={nextQ} className="btn-bounce" style={{
                width: "100%", background: `linear-gradient(135deg,${color},${dark})`,
                border: "none", borderRadius: 18, padding: "17px", fontSize: 18, fontWeight: 900,
                color: "#fff", cursor: "pointer", fontFamily: "Fredoka One",
                boxShadow: `0 8px 24px ${color}55`,
              }}>
                Next Question →
              </button>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

// ─── Hub ─────────────────────────────────────────────────────────────────────

export default function ScenariosScreen({ tier }: { tier: string }) {
  const isDark = tier === "master";
  const accentColor = isDark ? "#6366f1" : "#f97316";
  const tierKey = tier as ScenarioTier;

  const [filter, setFilter]           = useState<ScenarioTopic | "all">("all");
  const [active, setActive]           = useState<Scenario | null>(null);

  if (active) {
    return <ScenarioDetail scenario={active} isDark={isDark} onBack={() => setActive(null)} />;
  }

  const tierScenarios = SCENARIOS.filter(s => s.tiers.includes(tierKey));
  const visible = filter === "all" ? tierScenarios : tierScenarios.filter(s => s.topic === filter);

  return (
    <div style={{ fontFamily: "Nunito", minHeight: "100vh", background: isDark ? "#0f172a" : "#fff7ed", paddingBottom: 100 }}>

      {/* Header */}
      <div style={{
        background: isDark
          ? "linear-gradient(135deg,#1e3a5f,#0f172a)"
          : "linear-gradient(135deg,#f97316,#ec4899)",
        padding: "22px 18px 20px",
        position: "relative", overflow: "hidden",
      }}>
        <div style={{ position: "absolute", top: -20, right: -10, fontSize: 90, opacity: 0.07 }}>🎭</div>

        {/* Stash the Squirrel mascot row */}
        <div style={{ display: "flex", alignItems: "flex-end", gap: 14, marginBottom: 14 }}>
          <div style={{ position: "relative", flexShrink: 0 }}>
            <div style={{
              width: 68, height: 68, borderRadius: "50%",
              background: isDark ? "#334155" : "rgba(255,255,255,0.25)",
              border: isDark ? "3px solid #6366f1" : "3px solid rgba(255,255,255,0.6)",
              display: "flex", alignItems: "center", justifyContent: "center",
              fontSize: 40, boxShadow: "0 4px 18px #0003",
            }}>🐿️</div>
            <div style={{
              position: "absolute", bottom: -2, right: -2,
              background: isDark ? "#6366f1" : "#fff",
              borderRadius: "50%", width: 22, height: 22,
              display: "flex", alignItems: "center", justifyContent: "center",
              fontSize: 12, border: isDark ? "2px solid #1e293b" : "2px solid #f97316",
            }}>⚡</div>
          </div>
          <div>
            <div style={{ fontSize: 10, fontWeight: 900, color: isDark ? "#818cf8" : "rgba(255,255,255,0.75)", letterSpacing: 2, textTransform: "uppercase", marginBottom: 2 }}>
              Your Guide
            </div>
            <div style={{ fontSize: 20, fontWeight: 900, color: "#fff", fontFamily: "Fredoka One", lineHeight: 1, marginBottom: 3 }}>
              Stash the Squirrel 🐿️
            </div>
            <div style={{ fontSize: 12, color: isDark ? "#94a3b8" : "rgba(255,255,255,0.85)", fontWeight: 700, lineHeight: 1.4 }}>
              "Every good choice starts with knowing your options!"
            </div>
          </div>
        </div>

        <div style={{ fontSize: 11, fontWeight: 700, color: isDark ? "#475569" : "#ffffff99", letterSpacing: 3, textTransform: "uppercase", marginBottom: 4 }}>
          Real-life choices
        </div>
        <div style={{ fontSize: 26, fontWeight: 900, color: "#fff", fontFamily: "Fredoka One", lineHeight: 1, marginBottom: 6 }}>
          Money Scenes 🎭
        </div>
        <div style={{ fontSize: 13, color: isDark ? "#64748b" : "#ffffffcc", fontWeight: 700 }}>
          {tierScenarios.length} real-money decisions — what would you do?
        </div>
      </div>

      {/* Filter chips */}
      <div style={{
        position: "sticky", top: 0, zIndex: 10,
        background: isDark ? "#0f172a" : "#fff",
        borderBottom: isDark ? "1px solid #1e293b" : "2px solid #fef3c7",
        padding: "10px 16px",
      }}>
        <div style={{ display: "flex", gap: 8, overflowX: "auto", paddingBottom: 2, scrollbarWidth: "none" }}>
          {/* All chip */}
          <button onClick={() => setFilter("all")} className="btn-bounce" style={{
            flexShrink: 0,
            background: filter === "all" ? accentColor : isDark ? "#1e293b" : "#f3f4f6",
            border: `2px solid ${filter === "all" ? accentColor : isDark ? "#334155" : "#e5e7eb"}`,
            borderRadius: 20, padding: "6px 14px", fontSize: 12, fontWeight: 900,
            color: filter === "all" ? "#fff" : isDark ? "#94a3b8" : "#374151",
            cursor: "pointer", transition: "all 0.2s",
          }}>
            All ({tierScenarios.length})
          </button>
          {TOPICS.map(t => {
            const m = TOPIC_META[t];
            const count = tierScenarios.filter(s => s.topic === t).length;
            const active = filter === t;
            return (
              <button key={t} onClick={() => setFilter(t)} className="btn-bounce" style={{
                flexShrink: 0,
                background: active ? m.color : isDark ? "#1e293b" : "#f3f4f6",
                border: `2px solid ${active ? m.color : isDark ? "#334155" : "#e5e7eb"}`,
                borderRadius: 20, padding: "6px 14px", fontSize: 12, fontWeight: 900,
                color: active ? "#fff" : isDark ? "#94a3b8" : "#374151",
                cursor: "pointer", transition: "all 0.2s",
              }}>
                {m.emoji} {m.label} ({count})
              </button>
            );
          })}
        </div>
      </div>

      {/* Cards */}
      <div style={{ padding: "14px 16px", display: "flex", flexDirection: "column", gap: 12 }}>
        {visible.length === 0 && (
          <div style={{ textAlign: "center", padding: "40px 0", color: isDark ? "#475569" : "#9ca3af", fontWeight: 700 }}>
            No scenarios yet for this topic.
          </div>
        )}
        {visible.map(scenario => {
          const m = TOPIC_META[scenario.topic];
          return (
            <button key={scenario.id} onClick={() => setActive(scenario)} className="btn-bounce" style={{
              background: isDark ? "#1e293b" : "#fff",
              border: isDark ? `2px solid ${m.color}33` : `2.5px solid ${m.color}30`,
              borderRadius: 18, padding: 0, cursor: "pointer",
              textAlign: "left", overflow: "hidden",
              boxShadow: isDark ? `0 4px 16px ${m.color}10` : `0 4px 16px ${m.color}18`,
              transition: "box-shadow 0.2s, transform 0.2s",
            }}>
              <div style={{ padding: "14px 16px" }}>
                {/* Topic badge */}
                <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 10 }}>
                  <div style={{
                    display: "inline-flex", alignItems: "center", gap: 5,
                    background: `${m.color}18`, border: `1.5px solid ${m.color}44`,
                    borderRadius: 20, padding: "3px 10px",
                  }}>
                    <span style={{ fontSize: 11 }}>{m.emoji}</span>
                    <span style={{ fontSize: 10, fontWeight: 900, color: m.color, letterSpacing: 0.5 }}>{m.label.toUpperCase()}</span>
                  </div>
                  <div style={{ fontSize: 22 }}>▶</div>
                </div>

                {/* Title */}
                <div style={{ fontSize: 17, fontWeight: 900, color: isDark ? "#f1f5f9" : m.dark, fontFamily: "Fredoka One", marginBottom: 6, lineHeight: 1.2 }}>
                  {scenario.title}
                </div>

                {/* Character + setup preview */}
                <div style={{ display: "flex", alignItems: "flex-start", gap: 10 }}>
                  <div style={{ fontSize: 26, flexShrink: 0, lineHeight: 1, marginTop: 2 }}>{scenario.characterEmoji}</div>
                  <div>
                    <div style={{ fontSize: 11, fontWeight: 800, color: m.color, marginBottom: 3 }}>{scenario.character}</div>
                    <div style={{ fontSize: 13, fontWeight: 700, color: isDark ? "#94a3b8" : "#6b7280", lineHeight: 1.5, display: "-webkit-box", WebkitLineClamp: 2, WebkitBoxOrient: "vertical", overflow: "hidden" }}>
                      {scenario.setup}
                    </div>
                  </div>
                </div>
              </div>
            </button>
          );
        })}
      </div>
    </div>
  );
}
